import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { useLoad } from '../hooks';
import { PageHead } from '../Layout';
import { isActiveOutputType, trendArticleSourcePost } from '../../shared/types';
import { formatDateTime, formatRelative, formatStoredPublishError } from '../format';
import { wordCount } from '../../shared/types';
import type { ArticleSummary, Span } from '../../shared/types';

/** Read-only record of a legacy composed piece and its source provenance. */

interface VersionRow {
  id: number;
  versionNo: number;
  title: string | null;
  spans: Span[];
  bodyMd: string | null;
  author: 'composer' | 'publisher';
  createdAt: string;
}

interface MediaRef {
  id: number;
  kind?: string;
  title: string | null;
  author: string | null;
  mime?: string | null;
}

interface MessageRef {
  id: number;
  text: string | null;
  sentAt: string;
  author: string | null;
}

interface ArticleDetailResponse extends ArticleSummary {
  snapshots: {
    systemDraft: VersionRow | null;
    published: VersionRow | null;
  };
  idea: {
    id: number;
    kind: string;
    title: string | null;
    coreClaim: string | null;
    stage: string;
    approvalChatId: number | null;
    approvalMessageId: number | null;
  } | null;
  media: MediaRef[];
  messages: MessageRef[];
  cover: MediaRef | null;
  warnings?: string[];
}

function VersionText({ version }: { version: VersionRow }) {
  return (
    <div style={{ border: '1px solid var(--line)', padding: 16, minWidth: 0 }}>
      {version.title?.trim() ? <h3 style={{ marginTop: 0 }}>{version.title}</h3> : null}
      <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{version.bodyMd || '(empty)'}</div>
    </div>
  );
}

function groupByPara(spans: Span[]): Array<{ para: number; spans: Array<Span & { index: number }> }> {
  const map = new Map<number, Array<Span & { index: number }>>();
  spans.forEach((span, index) => {
    const key = Number.isFinite(span.para) ? span.para : 0;
    const bucket = map.get(key);
    if (bucket) bucket.push({ ...span, index });
    else map.set(key, [{ ...span, index }]);
  });
  return [...map.entries()].sort((a, b) => a[0] - b[0]).map(([para, group]) => ({ para, spans: group }));
}

export default function ArticleDetail() {
  const { id } = useParams();
  const articleId = Number(id);

  const [article, setArticle] = useState<ArticleDetailResponse | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);

  const { loading, error } = useLoad(
    useCallback(async () => {
      const res = await api.get<ArticleDetailResponse>(`/articles/${articleId}`);
      setArticle(res);
    }, [articleId]),
    { enabled: Number.isFinite(articleId) },
  );

  useEffect(() => {
    const coverId = article?.coverMediaAssetId;
    if (!coverId) {
      setCoverUrl(null);
      return;
    }
    api
      .get<{ url: string }>(`/media/${coverId}/url`)
      .then((r) => setCoverUrl(r.url))
      .catch(() => setCoverUrl(null));
  }, [article?.coverMediaAssetId]);

  const provenance = useMemo(() => {
    const byMedia = new Map((article?.media ?? []).map((m) => [m.id, m]));
    const byMessage = new Map((article?.messages ?? []).map((m) => [m.id, m]));
    return (span: Span): string => {
      if (span.sourceType === 'message') {
        const m = byMessage.get(span.sourceId);
        if (!m) return `message #${span.sourceId} (not found)`;
        return `message #${m.id} · ${m.author ?? 'unknown'} · ${formatDateTime(m.sentAt)}`;
      }
      const m = byMedia.get(span.sourceId);
      const label = span.sourceType === 'media' ? 'transcript' : 'source document';
      if (!m) return `${label} #${span.sourceId} (not found)`;
      return `${label} #${m.id} · ${[m.author, m.title].filter(Boolean).join(', ') || 'unattributed'}`;
    };
  }, [article]);

  if (loading) {
    return (
      <>
        <PageHead title={`Article ${id ?? ''}`} />
        <div className="empty">Loading…</div>
      </>
    );
  }

  if (!article) {
    return (
      <>
        <PageHead title={`Article ${id ?? ''}`} />
        <p className="fail">{error ?? 'not found'}</p>
      </>
    );
  }

  const activeFormat = isActiveOutputType(article.format);
  const paras = groupByPara(article.spans ?? []);
  const words = (article.spans ?? []).reduce((n, s) => n + wordCount(s.text), 0);
  const quotedPost = article.meta?.quotedPost;
  const trendPost = trendArticleSourcePost(article.meta);
  const quotedPostUrl =
    quotedPost &&
    typeof quotedPost === 'object' &&
    typeof (quotedPost as Record<string, unknown>).url === 'string'
      ? String((quotedPost as Record<string, unknown>).url)
      : null;
  const replyTarget = article.meta?.replyTarget;
  const replyTargetUrl =
    replyTarget &&
    typeof replyTarget === 'object' &&
    typeof (replyTarget as Record<string, unknown>).url === 'string'
      ? String((replyTarget as Record<string, unknown>).url)
      : null;

  return (
    <>
      <PageHead
        title={article.title?.trim() || `${article.format} ${article.id}`}
        subtitle={`${article.format} · ${words} words · ${
          (article.spans ?? []).length
        } spans · updated ${formatRelative(article.updatedAt)}`}
      />

      {error ? <p className="fail">{error}</p> : null}
      <p className="editorial-alert">
        This article is read-only history. Manage current work in <Link to="/dashboard">Publications</Link>.
      </p>
      {!activeFormat ? <p className="muted">This retired output is preserved as a read-only historical record.</p> : null}

      <div className="meta-grid" style={{ marginBottom: 24 }}>
        <span className="label">Stage</span>
        <span>
          <span className="chip on">
            {article.idea?.stage.replace('_', ' ') ?? '—'}
          </span>
          {article.xUrl ? (
            <>
              {' '}
              <a href={article.xUrl} target="_blank" rel="noreferrer">
                {article.xUrl}
              </a>
            </>
          ) : null}
        </span>
        <span className="label">Delivery</span>
        <span className={article.deliveryStatus === 'failed' ? 'fail' : 'faint'}>
          {article.deliveryStatus}
        </span>
        <span className="label">Idea</span>
        <span>
          {article.idea ? (
            <Link to={`/ideas/${article.idea.id}`}>
              Idea #{article.idea.id}
            </Link>
          ) : (
            '—'
          )}
        </span>
        <span className="label">Published</span>
        <span className="faint">{formatDateTime(article.publishedAt)}</span>
        {quotedPostUrl ? (
          <>
            <span className="label">Quoted post</span>
            <span><a href={quotedPostUrl} target="_blank" rel="noreferrer">{quotedPostUrl}</a></span>
          </>
        ) : null}
        {trendPost ? (
          <>
            <div className="label">Trend source</div>
            <span><a href={trendPost.url} target="_blank" rel="noreferrer">Embedded source post on X ↗</a></span>
          </>
        ) : null}
        {replyTargetUrl ? (
          <>
            <span className="label">Target post</span>
            <span><a href={replyTargetUrl} target="_blank" rel="noreferrer">{replyTargetUrl}</a></span>
          </>
        ) : null}
        {article.deliveryStatus === 'failed' && article.publishError ? (
          <>
            <span className="label">Last publish error</span>
            <span className="fail" title={article.publishError}>
              {activeFormat ? formatStoredPublishError(article.publishError) : article.publishError}
            </span>
          </>
        ) : null}
      </div>

      {article.snapshots.systemDraft && article.snapshots.published ? (
        <section style={{ marginBottom: 32 }}>
          <div className="label" style={{ marginBottom: 8 }}>Publication record</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            <div>
              <div className="faint mono" style={{ marginBottom: 6 }}>
                System draft
              </div>
              <VersionText version={article.snapshots.systemDraft} />
            </div>
            <div>
              <div className="faint mono" style={{ marginBottom: 6 }}>
                Published on X
              </div>
              <VersionText version={article.snapshots.published} />
            </div>
          </div>
        </section>
      ) : article.deliveryStatus === 'published' ? (
        <div className="empty" style={{ marginBottom: 28 }}>
          This legacy publication does not yet have a verified publication snapshot.
        </div>
      ) : null}

      {/* ---- cover ---- */}
      {article.cover ? (
        <div style={{ marginBottom: 24 }}>
          <div className="label" style={{ marginBottom: 6 }}>
            Cover
          </div>
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={article.cover.title ?? 'cover'}
              style={{ maxWidth: 420, width: '100%', border: '1px solid var(--line)' }}
            />
          ) : (
            <div className="faint">media #{article.cover.id} (no preview)</div>
          )}
          <div className="faint" style={{ fontSize: 12 }}>
            {[article.cover.author, article.cover.title].filter(Boolean).join(', ') ||
              `media #${article.cover.id}`}
          </div>
        </div>
      ) : null}

      {/* ---- body / editor ---- */}
      <div className="toolbar">
        <span className="label">Composed body · read-only history</span>
      </div>

      <article style={{ maxWidth: '68ch', marginBottom: 32 }}>
          {paras.length === 0 ? (
            <div className="empty">No spans.</div>
          ) : (
            paras.map(({ para, spans }) => (
              <div key={para} style={{ marginBottom: 16 }}>
                {spans.map((span) =>
                  span.quote ? (
                    <blockquote
                      key={span.index}
                      title={provenance(span)}
                      style={{
                        margin: '12px 0',
                        paddingLeft: 16,
                        borderLeft: '2px solid var(--line-strong)',
                        color: 'var(--muted)',
                      }}
                    >
                      {span.text}
                      <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
                        {provenance(span)}
                      </div>
                    </blockquote>
                  ) : (
                    <span
                      key={span.index}
                      title={provenance(span)}
                      style={{
                        borderBottom: '1px dotted var(--line-strong)',
                        marginRight: 4,
                        cursor: 'help',
                      }}
                    >
                      {span.text}
                    </span>
                  ),
                )}
              </div>
            ))
          )}
      </article>

      {/* ---- provenance table ---- */}
      <div className="label" style={{ marginBottom: 8 }}>
        Provenance
      </div>
      <div className="table-scroll" style={{ marginBottom: 32 }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: 50 }}>#</th>
              <th>Words</th>
              <th style={{ width: 320 }}>Source</th>
            </tr>
          </thead>
          <tbody>
            {(article.spans ?? []).map((span, i) => (
              <tr key={i}>
                <td className="mono">{i + 1}</td>
                <td>{span.text}</td>
                <td className="faint">{provenance(span)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </>
  );
}
